"use client";

import { useEffect, useState } from "react";
import { createClient } from "@/lib/supabase/client";

type Props = {
  firstNameLabel?: string;
  lastNameLabel?: string;
  dateLabel?: string;
  emailLabel?: string;
  messageLabel?: string;
  buttonText?: string;
};

function splitFullName(fullName: string | undefined | null) {
  if (!fullName) return { firstName: "", lastName: "" };
  const [firstName = "", ...rest] = fullName.trim().split(/\s+/);
  return { firstName, lastName: rest.join(" ") };
}

export function ContactForm({
  firstNameLabel,
  lastNameLabel,
  dateLabel,
  emailLabel,
  messageLabel,
  buttonText,
}: Props) {
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");

  useEffect(() => {
    const supabase = createClient();
    supabase.auth.getUser().then(({ data }) => {
      const user = data.user;
      if (!user) return;
      const { firstName: f, lastName: l } = splitFullName(
        user.user_metadata?.full_name as string | undefined
      );
      if (f) setFirstName((prev) => prev || f);
      if (l) setLastName((prev) => prev || l);
      if (user.email) setEmail((prev) => prev || user.email!);
    });
  }, []);

  return (
    <form className="space-y-4 rounded-none border border-slate-200 bg-white p-6 shadow-soft backdrop-blur">
      <div className="grid gap-4 sm:grid-cols-2">
        <label className="text-xs uppercase tracking-[0.3em] text-slate-500">
          {firstNameLabel}
          <input
            type="text"
            required
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            className="mt-2 w-full rounded-xl border border-slate-300 bg-white px-3 py-2 text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-200"
            placeholder="First Name"
            autoComplete="given-name"
          />
        </label>
        <label className="text-xs uppercase tracking-[0.3em] text-slate-500">
          {lastNameLabel} <span className="text-slate-400">(optional)</span>
          <input
            type="text"
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
            className="mt-2 w-full rounded-xl border border-slate-300 bg-white px-3 py-2 text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-200"
            placeholder="Last Name"
            autoComplete="family-name"
          />
        </label>
      </div>
      <label className="block text-xs uppercase tracking-[0.3em] text-slate-500">
        {dateLabel} <span className="text-slate-400">(optional)</span>
        <input
          type="date"
          className="mt-2 w-full rounded-xl border border-slate-300 bg-white px-3 py-2 text-slate-900 focus:outline-none focus:ring-2 focus:ring-slate-200"
        />
      </label>
      <label className="block text-xs uppercase tracking-[0.3em] text-slate-500">
        {emailLabel}
        <input
          type="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className="mt-2 w-full rounded-xl border border-slate-300 bg-white px-3 py-2 text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-200"
          placeholder="you@example.com"
          autoComplete="email"
        />
      </label>
      <label className="block text-xs uppercase tracking-[0.3em] text-slate-500">
        {messageLabel}
        <textarea
          required
          className="mt-2 min-h-[140px] w-full rounded-2xl border border-slate-300 bg-white px-3 py-2 text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-200"
          placeholder="Tell us about your event..."
        />
      </label>
      <button
        type="button"
        className="w-full rounded-none bg-[var(--primary)] px-6 py-3 text-sm font-semibold uppercase tracking-[0.4em] text-white shadow-soft transition hover:-translate-y-0.5"
      >
        {buttonText}
      </button>
    </form>
  );
}
